import { VerificationNames, VerificationStatus } from "shared/types";
import { has, formatToCurrency } from "shared/utils";
import { Cell } from "views/applications-id/components/ApplicantsTable/components/Cell";
import { ReportStatusBadge } from "views/applications-id/components/ApplicantsTable/components/ReportStatusBadge";
import {
  FullApplicant,
  Verification
} from "views/applications-id/components/ApplicantsTable/types";
import { useAuthContext } from "providers/Auth";
import { DataWithAuditFlag } from "./components/DataWithAuditFlag";
export const IncomeReportSection = ({
  applicant,
  verifications,
  hasAuditFlagEnabled
}: {
  applicant: FullApplicant;
  hasAuditFlagEnabled: boolean;
  verifications: Verification[];
}) => {
  const { permissions } = useAuthContext();

  const verificationsMap = verifications.reduce(
    (acc, el) => {
      acc[el.name] = el;
      return acc;
    },
    {} as Record<string, Verification>
  );

  const selfReportedVerification = verificationsMap[
    VerificationNames.IncomeVerification
  ] as Verification | undefined;
  const verifiedIncomeVerification =
    verificationsMap[VerificationNames.VerifiedIncomeVerification];

  const statusSet = new Set(verifications.map((el) => el.status));

  const isFailed = statusSet.has(VerificationStatus.Failed);
  const isFinishedWithWarnings = statusSet.has(
    VerificationStatus.FinishedWithWarnings
  );
  const isInProgress = statusSet.has(VerificationStatus.Started);
  const isPending = statusSet.has(VerificationStatus.Pending);

  const selfReportFinishedVerifiedIncomeInProgress =
    selfReportedVerification &&
    selfReportedVerification.status === VerificationStatus.Finished &&
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    verifiedIncomeVerification &&
    [VerificationStatus.Started, VerificationStatus.Submitted].includes(
      verifiedIncomeVerification.status
    );

  const mergedStatus = isFailed
    ? VerificationStatus.Failed
    : isFinishedWithWarnings
      ? VerificationStatus.FinishedWithWarnings
      : isInProgress || selfReportFinishedVerifiedIncomeInProgress
        ? VerificationStatus.Started
        : isPending
          ? VerificationStatus.Pending
          : VerificationStatus.Finished;

  return (
    <>
      {/* Empty cell for all rows */}
      <Cell className="bg-background-900" />
      <Cell
        isDataCell
        className="relative"
        isPermitted={permissions.canAccessReports.incomeReport}
        showRestrictedFlag={!permissions.canAccessReports.incomeReport}
      >
        <DataWithAuditFlag
          hasData={has(verifications[0].summary?.gross_monthly_income)}
          dataValue={
            verifications[0].summary?.gross_monthly_income
              ? formatToCurrency(verifications[0].summary.gross_monthly_income)
              : null
          }
          hasAuditFlagEnabled={hasAuditFlagEnabled}
          auditSummaryFlags={applicant.audit_summary_flags}
        />
        <ReportStatusBadge verificationStatus={mergedStatus} />
      </Cell>
      <Cell isDataCell isPermitted={permissions.canAccessReports.incomeReport}>
        <DataWithAuditFlag
          hasData={has(verifications[0].summary?.net_monthly_income)}
          dataValue={
            verifications[0].summary?.net_monthly_income
              ? formatToCurrency(verifications[0].summary.net_monthly_income)
              : null
          }
          hasAuditFlagEnabled={hasAuditFlagEnabled}
          auditSummaryFlags={applicant.audit_summary_flags}
        />
      </Cell>
      <Cell isDataCell isPermitted={permissions.canAccessReports.incomeReport}>
        <DataWithAuditFlag
          hasData={has(verifications[1]?.summary?.gross_monthly_income)}
          dataValue={
            verifications[1]?.summary?.gross_monthly_income
              ? formatToCurrency(verifications[1].summary.gross_monthly_income)
              : null
          }
          hasAuditFlagEnabled={hasAuditFlagEnabled}
          auditSummaryFlags={applicant.audit_summary_flags}
        />
      </Cell>
    </>
  );
};
